import React, { createContext, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';

import comActions from '../../../Actions/commonActions';

import useModal from '../../../Hooks/useModal';
import Spinner from '../Spinner/Spinner';

import './StandardModal.css';

const ModalContext = createContext();

export const ModalSize = {
    SMALL: 'modal-sm',
    LARGE: 'modal-lg',
    X_LARGE: 'modal-xl',
    FULL: 'modal-full',
};

const FIXED_HEIGHT_SIZES = [ModalSize.LARGE, ModalSize.X_LARGE, ModalSize.FULL];

export const ModalButtonType = {
    DEFAULT: 0,
    SEND: 1,
    SAVE: 2,
    DELETE: 3
};

/**
 * BaseModal: A minimal modal component that renders its children in order without styling. It is preferrable to use the StandardModal component
 * instead and should primarily be used in specific cases where custom rendering is required to avoid conflicts like duplicate headers or buttons.
 *
 * Props:
 * @param {string} size - Modal size.
 * @param {boolean} isLoading - Show/hide loading spinner.
 * @param {function} onCancel - Callback for cancel action.
 */
export const BaseModal = ({ size, isLoading, onCancel, children }) => {
    const dialogRef = useRef(null);
    const { closeModal } = useModal();

    const modalClasses = `base-modal modal-dialog ${size ? size : ''} ${FIXED_HEIGHT_SIZES.includes(size) ? 'h-100' : ''}`;

    useEffect(() => {
        dialogRef.current?.showModal();
    }, []);

    const onCancelHandler = () => {
        if (onCancel) onCancel();
        closeModal();
    };

    return (
        <dialog ref={dialogRef} className={modalClasses} onCancel={onCancelHandler} tabIndex="-1" role="dialog" aria-labelledby="modal-title">
            <div className="modal-content">
                {isLoading ? (
                    <div className="d-flex flex-grow-1 align-items-center justify-content-center py-48">
                        <Spinner />
                    </div>
                ) : (
                    children
                )}
            </div>
        </dialog>
    );
};

/**
 * StandardModal: A structured modal with automatic title, sections, and buttons. If needed, passing ModalHeader, ModalFooter, and ModalBody components ensures proper placement and styling in their respective sections.
 *
 * Props:
 * @param {string} title - Modal title.
 * @param {boolean} isLoading - Show/hide loading spinner.
 * @param {string} size - Modal width based on design specs.
 * @param {string} type - Button text type.
 * @param {function} onClose - Callback for close action.
 * @param {function} onSubmit - Callback for submit action, renders a submit button.
 * @param {boolean} submitDisabled - Disables the submit button when true.
 * @param {string} submitLabel - Overrides the submit button text chosen by `type`.
 * @param {string} cancelLabel - Overrides the cancel button text.
 */
export const StandardModal = ({ title, isLoading, size, type, onClose, onSubmit, submitDisabled, submitLabel, cancelLabel, children }) => {
    const { closeModal } = useModal();

    const childrenArray = React.Children.toArray(children);
    const header = childrenArray.find((child) => child.type === ModalHeader);
    const body = childrenArray.find((child) => child.type === ModalBody);
    const footer = childrenArray.find((child) => child.type === ModalFooter);
    const bodyChildren = childrenArray.filter((child) => child.type !== ModalHeader && child.type !== ModalFooter && child.type !== ModalBody);

    const onCloseHandler = () => {
        if (onClose) onClose();
        closeModal();
    };

    return (
        <ModalContext.Provider value={{ onSubmit, onClose }}>
            <BaseModal size={size} isLoading={isLoading} onCancel={onClose}>
                {!header && (
                    <ModalHeader onclose={onCloseHandler}>
                        <ModalHeaderTitle>{title}</ModalHeaderTitle>
                    </ModalHeader>
                )}

                {!body ? <ModalBody>{bodyChildren}</ModalBody> : <> {body} </>}

                {!footer ? (
                    <ModalFooter>
                        <ModalFooterButtons type={type} onClose={onCloseHandler} onSubmit={onSubmit} submitDisabled={submitDisabled} submitLabel={submitLabel} cancelLabel={cancelLabel} />
                    </ModalFooter>
                ) : (
                    <>{footer}</>
                )}
            </BaseModal>
        </ModalContext.Provider>
    );
};

export const ModalHeader = ({ onclose, children }) => {
    const alert = useSelector((state) => state.CommonReducer?.alert);

    return (
        <>
            <div className="modal-header">
                <button type="button" className="close" aria-label="Close" onClick={onclose} />

                {children}
            </div>

            {alert && <ModalAlert alert={alert} />}
        </>
    );
};

export const ModalHeaderTitle = ({ children }) => {
    return <h4 className="modal-title"> {children}</h4>;
};

export const ModalBody = ({ children }) => {
    return <div className="modal-body"> {children}</div>;
};

export const ModalFooter = ({ children }) => {
    return <div className="modal-footer d-flex"> {children}</div>;
};

export const ModalFooterButtons = ({ onClose, onSubmit, type = ModalButtonType.DEFAULT, submitDisabled = false, submitLabel, cancelLabel }) => {
    const { t } = useTranslation();

    const buttonTexts = [
        { submit: t('baseModal.submit'), cancel: t('baseModal.cancel') },
        { submit: t('baseModal.send'), cancel: t('baseModal.cancel') },
        { submit: t('baseModal.save'), cancel: t('baseModal.cancel') },
        { submit: t('baseModal.delete'), cancel: t('baseModal.cancel') },
    ];

    return (
        <div className="w-100 d-flex justify-content-end gap-8">
            {onSubmit && (
                <button type="button" className="btn btn-primary btn-sm" onClick={onSubmit} disabled={submitDisabled}>
                    {submitLabel ?? buttonTexts[type].submit}
                </button>
            )}
            <button type="button" className="btn btn-sm" onClick={onClose}>
                {cancelLabel ?? buttonTexts[type].cancel}
            </button>
        </div>
    );
};

export const ModalAlert = ({ alert }) => {
    const dispatch = useDispatch();
    if (!alert) return null;
    const commonActions = bindActionCreators(comActions, dispatch);
    let message = alert.message;

    if (message && typeof message === 'number') {
        message = message.toString();
    }

    return (
        <div className={`modal-alert alert alert-${alert.type} flex-none min-w-0 mw-100 mx-24 mt-24 mb-0`}>
            <button type="button" className="close" aria-label="Close" onClick={commonActions.hideAlert} />
            <p className="bold m-0 p-0">{alert.title}</p>

            {message &&
                message.split('\n').map((item, index) => {
                    return (
                        <span key={index}>
                            {item}
                            <br />
                        </span>
                    );
                })}
        </div>
    );
};

export default StandardModal;
